import { AuthService } from '../../../../services/auth-service';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { PackagingUnitNavigationService } from 'src/app/navigation/services/navigation-services/packaging-unit-navigation.service';
import { DirectoryApiService } from 'src/app/data-transfer/services/directory-api-service';
import { PackagingUnitApiService } from 'src/app/data-transfer/services/packaging-unit-api-service';
import { PackagingSystemApiService } from 'src/app/data-transfer/services/packaging-system-api-service';
import { PackagingSystemNavigationService } from 'src/app/navigation/services/navigation-services/packaging-system-navigation.service';
import { AnalysisMenuCaller } from 'src/app/components/shared-components/analyses-menu/analyses-menu-parent/analyses-menu-parent.component';
import { Subscription } from 'rxjs';
import { PackagingPart } from 'src/app/model/packaging-part-enum';
import { LicenseService } from 'src/app/services/licensing-service';
import { TemplateApiService } from 'src/app/data-transfer/services/template-api-service';
import { downloadDocument } from 'src/app/services/document-download';
import { TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-packaging-navigation',
  templateUrl: './packaging-navigation.component.html',
  styleUrls: ['./packaging-navigation.component.scss'],
})
export class PackagingNavigationComponent implements OnInit, OnDestroy {

  @Input() itemId = -1;
  @Input() versionId = -1;
  @Input() callerLevelId!: number;
  @Input() callerTypeId?: number;
  @Input() currentDirectoryId?: number;
  @Input() creationTimestamp!: Date;
  @Input() isGeneric = false;
  @Input() isPreview = false;
  @Input() isCurrentVersion = false;
  @Input() hasRecyclabilityResult = false;
  @Input() hasLifeCycleResult = false;
  @Input() public brandName = '';
  @Input() public productName = '';
  @Input() public articleNumber = '';

  isPackagingUnit = true;
  isWriteAllowed = true;
  isUserValidator = false;
  isValidExcelStructure = false;
  callerPageId = AnalysisMenuCaller.Sidenav;

  protected permissionsSubscription?: Subscription;
  protected downloadSubscription?: Subscription;
  protected excelSubscription?: Subscription;
  protected checkSubscription?: Subscription;


  constructor(
    private unitNavigationService: PackagingUnitNavigationService,
    private systemNavigationService: PackagingSystemNavigationService,
    private directoryApiService: DirectoryApiService,
    private packagingUnitApiService: PackagingUnitApiService,
    private packagingSystemApiService: PackagingSystemApiService,
    private authService: AuthService,
    private licenseService: LicenseService,
    private templateApiService: TemplateApiService,
    private translateService: TranslocoService
  ) {
    this.isUserValidator = this.authService.isUserValidator();
  }

  ngOnInit(): void {
    if (this.currentDirectoryId != null) {
      this.permissionsSubscription = this.directoryApiService.determineCurrentUserPermissions(this.currentDirectoryId)
        .subscribe(result =>
          this.isWriteAllowed = result.write
        );
    }
    this.isPackagingUnit = this.callerLevelId === PackagingPart.Unit;
    this.checkPackagingUnit();
  }

  getProductNameText() {
    return this.itemId != null ? this.brandName + ' / ' + this.productName : '---';
  }

  getArticleNumberText() {
    return this.itemId != null ? this.articleNumber ?? '' : '';
  }

  navigateToItem(versionId?: number) {
    if (this.itemId == null) { return; }
    switch (this.callerLevelId) {
      case PackagingPart.System:
        this.systemNavigationService.navigateToPackagingSystem(this.itemId, versionId);
        break;
      case PackagingPart.Unit:
        this.unitNavigationService.navigateToPackagingUnit(this.itemId, versionId);
        break;
      default: break;
    }
  }

  downloadPackagingUnitJson() {
    this.downloadSubscription = this.packagingUnitApiService.downloadPackagingUnitsJson([{ id: this.itemId, version: this.versionId }])
      .subscribe(zipFile => {
        const zipUrl = window.URL.createObjectURL(zipFile);
        window.open(zipUrl, '_blank');
      });
  }

  downloadPackagingSystemsJson() {
    this.downloadSubscription = this.packagingSystemApiService.downloadPackagingSystemsJson([{ id: this.itemId, version: this.versionId }])
      .subscribe(zipFile => {
        const zipUrl = window.URL.createObjectURL(zipFile);
        window.open(zipUrl, '_blank');
      });
  }

  downloadPackagingUnitExcel() {
    if(this.isValidExcelStructure) {
      this.downloadSubscription = this.templateApiService.getPUImporterTemplateWithData([{ id: this.itemId, version: this.versionId }])
        .subscribe(fileToSave => { downloadDocument(fileToSave);});
    }
  }

  checkPackagingUnit() {
    this.checkSubscription = this.templateApiService.checkPUsStructureForExcelTemplate([{ id: this.itemId, version: this.versionId }])
      .subscribe(isValid  => {this.isValidExcelStructure = isValid == true})
  }

  ngOnDestroy(): void {
    this.permissionsSubscription?.unsubscribe();
    this.downloadSubscription?.unsubscribe();
    this.excelSubscription?.unsubscribe();
    this.checkSubscription ?.unsubscribe();
  }
  getDeactivatedExcelDownloadTooltip() : string {
    if(!this.isValidExcelStructure) {
      return this.translateService.translate('sidenav.excelDownloadDeactivatedTooltip');
    }
    return '';
  }

  isLcaAllowed(): boolean {
    return this.licenseService.isCradleToGraveAllowed || this.licenseService.isCradleToGateAllowed;
  }

  isC2GateAllowed(): boolean {
    return this.licenseService.isCradleToGateAllowed;
  }

  isC2GraveAllowed(): boolean {
    return this.licenseService.isCradleToGraveAllowed;
  }

  isCCAllowed(): boolean {
    return this.licenseService.isCCAllowed;
  }

  isExcelDownloadAllowed(): boolean {
    return this.licenseService.isExcelDownloadAllowed;
  }
}
